import {
  Box,
  Separator,
  Flex,
  HStack,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useMemo } from "react";
import {
  MapComponent,
  MapMode,
  MapPageView,
} from "../../project/stores/MapStore";
import {
  AppHeader,
  AppHeaderHeight,
} from "../../shared/views/AppHeader/AppHeader";
import { LoadingHeader } from "./views/LoadingHeader";
import { NodeRefreshAlert } from "./views/NodeRefreshAlert";
import { FiltersBar } from "./views/FiltersBar/FiltersBar";
import { MainMap } from "./views/MainMap";
import { DetailBar } from "./views/DetailBar/DetailBar";
import { DashboardView } from "./views/dashboard/DashboardView";
import { ForecastHeader } from "./views/CraForecast/ForecastHeader";
import { Header } from "./views/Header/Header";
import { CodingPage } from "./views/coding/CodingPage";
import { LifeCycleStage } from "../../osaway/models/LifeCycleStage";
import { useParams, useSearchParams } from "react-router-dom";
import { useStores } from "../../shared/stores/RootStore";
import { PendingCoordChangesControls } from "./views/overlay/PendingCoordChangesControls";
import { MyProjectsList } from "../../project/views/MyProjectsList/MyProjectsList";
import { ProjectMapSelectionPopover } from "../home/ProjectMapSelectionPopover";
import { MapComponentVisibilityControls } from "./views/MapComponentVisibilityControls";
import { useAutoUpdateQueryParam } from "./hooks/useAutoUpdateQueryParam";
import { FetchingStatus } from "../../project/stores/LoadingStore";
import { Campaign } from "../../project/models/Campaign";
import { CampaignSelectionModal } from "./CampaignSelectionModal";
import { MapControls } from "./views/MapControls";
import { ShortcutList } from "./views/overlay/ShortcutList";
import { NodeList } from "./views/BottomBar/osa-way/NodeList";
import { ToggleVisibilityButton } from "./ToggleVisibilityButton";
import { MapLegend } from "./views/overlay/MapLegend";
import { ViewComments } from "./views/BottomBar/ViewComments";
import { HlaStreetView } from "../../hla/views/StreetView/HlaStreetView";

enum ParamNames {
  CampaignId = "campaignId",
  OsaWayId = "osaWayId",
  OsaNodeId = "osaNodeId",
  HlaWayId = "hlaWayId",
}

const MapPageBase: React.FC<{
  isShared?: boolean;
  isHome?: boolean;
}> = ({ isShared = false, isHome = false }) => {
  const {
    mapStore,
    filterStore,
    osaWayStore,
    osaNodeStore,
    craForecastStore,
    campaignStore,
    loadingStore,
    projectStore,
    dashboardStore,
    hlaStore,
    mapQueryStore,
  } = useStores();
  const osaWays = filterStore.originalOrForecastedOsaWays;
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignSelectionModal = useDisclosure();
  const allNodes = filterStore.originalOrForecastedNodes;
  const selectedOsaWay = osaWayStore.getSelected();
  const selectedHlaWay = hlaStore.getSelected();
  const selectedProject = projectStore.getSelected();

  useEffect(() => {
    const service = "Lane Patrol";
    if (selectedHlaWay) {
      document.title = `${selectedHlaWay.name} - ${service}`;
    } else if (selectedOsaWay) {
      document.title = `${selectedOsaWay.name} - ${service}`;
    } else if (selectedProject) {
      document.title = `${selectedProject.name} - ${service}`;
    } else {
      document.title = `My projects - ${service}`;
    }
  }, [selectedHlaWay, selectedOsaWay, selectedProject]);

  const preSelection: Record<ParamNames, string> = useMemo(() => {
    return {
      campaignId: searchParams.get(ParamNames.CampaignId) ?? "",
      osaWayId: searchParams.get(ParamNames.OsaWayId) ?? "",
      osaNodeId: searchParams.get(ParamNames.OsaNodeId) ?? "",
      hlaWayId: searchParams.get(ParamNames.HlaWayId) ?? "",
    };
    // eslint-disable-next-line
  }, [projectId]);

  useAutoUpdateQueryParam(
    ParamNames.CampaignId,
    campaignStore.selectedId,
    setSearchParams,
  );

  useAutoUpdateQueryParam(
    ParamNames.OsaWayId,
    osaWayStore.selectedId,
    setSearchParams,
  );

  useAutoUpdateQueryParam(
    ParamNames.OsaNodeId,
    osaNodeStore.selectedId,
    setSearchParams,
  );

  useAutoUpdateQueryParam(
    ParamNames.HlaWayId,
    hlaStore.selectedId,
    setSearchParams,
  );

  const onPageLoad = async () => {
    projectStore.resetState();
    osaWayStore.resetState();
    mapStore.resetState();
    filterStore.resetState();
    osaNodeStore.resetState();
    hlaStore.resetState();
    craForecastStore.resetState();
    dashboardStore.resetState();
    campaignStore.resetState();

    mapStore.refreshMapWindow();

    var scope = "Home";

    if (isHome) {
      mapStore.setMode(MapMode.HomePage);
      mapStore.recenterMap();

      loadingStore.setFetchingStatus({
        projectsList: FetchingStatus.NotStarted,
      });
      loadingStore.registerScope(scope);

      await loadingStore.loadProjectsList(scope);
    } else {
      mapStore.setMode(MapMode.ProjectPage);
      scope = projectId!;

      loadingStore.setFetchingStatus({
        selectedProject: FetchingStatus.NotStarted,
        campaigns: FetchingStatus.NotStarted,
        osaWays: FetchingStatus.NotStarted,
        hlaWays: FetchingStatus.NotStarted,
        dashboards: FetchingStatus.NotStarted,
        hlaForecastScenarios: FetchingStatus.NotStarted,
        craForecastScenarios: FetchingStatus.NotStarted,
        nodesAndMetadata: FetchingStatus.NotStarted,
      });
      loadingStore.registerScope(scope);

      await loadingStore.loadSelectedProject(projectId!, scope);

      if (!loadingStore.isScopeActive(scope)) return;

      mapStore.recenterMap();

      await loadingStore.loadCampaigns(projectId!, scope);
      if (!loadingStore.isScopeActive(scope)) return;

      if (preSelection[ParamNames.CampaignId]) {
        campaignStore.setSelected(preSelection[ParamNames.CampaignId]);
      } else if (campaignStore.campaigns.length === 1) {
        campaignStore.setSelected(campaignStore.campaigns[0].id);
      } else if (campaignStore.campaigns.length === 0) {
        console.log("No campaigns found. Creating a new one...");
        campaignStore.create({
          projectId: projectId!,
          name: new Date().getFullYear().toString(),
        });
      } else {
        campaignSelectionModal.onOpen();
      }

      await loadingStore.loadCraForecastScenarios(projectId!, scope);
      await loadingStore.loadHlaForecastScenarios(projectId!, scope);

      if (!loadingStore.isScopeActive(scope)) return;
      await loadingStore.loadDashboards(projectId!, scope);
    }

    mapStore.recenterMap();
  };

  const onSelectCampaign = async (campaign: Campaign) => {
    const scope = campaign.projectId;

    loadingStore.setFetchingStatus({
      osaWays: FetchingStatus.NotStarted,
      hlaWays: FetchingStatus.NotStarted,
      nodesAndMetadata: FetchingStatus.NotStarted,
    });
    loadingStore.registerScope(scope);

    if (!loadingStore.isScopeActive(scope)) return;
    await loadingStore.loadOsaWays(campaign.id, scope);

    if (preSelection[ParamNames.OsaWayId]) {
      osaWayStore.select(preSelection[ParamNames.OsaWayId]);
    }

    if (!loadingStore.isScopeActive(scope)) return;
    await loadingStore.loadOsaNodesAndMetadata(scope);

    if (preSelection[ParamNames.OsaNodeId]) {
      osaNodeStore.select(preSelection[ParamNames.OsaNodeId]);
    }

    if (!loadingStore.isScopeActive(scope)) return;
    await loadingStore.loadHlaWays(campaign.id, scope);

    if (preSelection[ParamNames.HlaWayId]) {
      hlaStore.select(preSelection[ParamNames.HlaWayId]);
    }

    mapStore.recenterMap();
  };

  useEffect(() => {
    if (campaignStore.selected) {
      onSelectCampaign(campaignStore.selected);
    }

    // eslint-disable-next-line
  }, [campaignStore.selected]);

  useEffect(() => {
    onPageLoad();

    // eslint-disable-next-line
  }, [projectId]);

  // TODO move the filtering triggers to the stores (trigger filter when the ways are updated)
  useEffect(() => {
    filterStore.filterOsaWays();
    // eslint-disable-next-line
  }, [osaWays]);

  useEffect(() => {
    filterStore.filterNodes();
    // eslint-disable-next-line
  }, [allNodes]);

  return (
    <Flex
      flexDir="column"
      maxHeight="100vh"
      maxWidth="100vw"
      flexGrow={1}
      overflow={"auto"}
    >
      <AppHeader>
        <Header isHome={isHome} />
      </AppHeader>
      <AppHeaderHeight />

      <NodeRefreshAlert />
      <ForecastHeader />

      <HStack
        align="stretch"
        gap={0}
        flexGrow={1}
        overflow="auto"
        separator={<Separator />}
      >
        {!isHome && mapStore.view === MapPageView.Dashboard && (
          <Stack flexGrow={1} overflow="auto" gap={0}>
            <DashboardView />
          </Stack>
        )}

        {isHome && <MyProjectsList />}

        {!isHome && mapStore.view === MapPageView.Map && (
          <>
            {mapStore.componentVisibility.detail && <DetailBar />}
            <ToggleVisibilityButton
              component={MapComponent.Detail}
              arrowAngleWhenOpen={180}
            />
          </>
        )}

        {mapStore.view === MapPageView.Map && (
          <Stack flexGrow={1} overflow="auto" gap={0}>
            {selectedOsaWay?.lifeCycleStage === LifeCycleStage.Coding &&
            loadingStore.fetchingState.nodesAndMetadata ===
              FetchingStatus.Done ? (
              <CodingPage />
            ) : (
              <Stack
                flexGrow={1}
                alignItems={"stretch"}
                gap={0}
                separator={<Separator />}
              >
                <Box flexGrow={1} position="relative">
                  <MainMap isHome={isHome} />
                  <MapControls />
                  {!isHome && <MapLegend />}

                  {isHome && <ProjectMapSelectionPopover />}

                  <Stack
                    bottom={16}
                    left={4}
                    position="absolute"
                    zIndex={1}
                    pointerEvents={"none"}
                    alignItems={"start"}
                    gap={4}
                  >
                    {!isHome && mapStore.componentVisibility.shortcuts && (
                      <ShortcutList />
                    )}
                    {!isHome && (
                      <MapComponentVisibilityControls
                        components={[MapComponent.Shortcuts]}
                      />
                    )}
                  </Stack>

                  <PendingCoordChangesControls />
                </Box>

                {!!selectedHlaWay && <HlaStreetView />}
              </Stack>
            )}

            {selectedOsaWay?.lifeCycleStage === LifeCycleStage.DataCollection &&
              !!osaNodeStore.nodesByWayId.get(selectedOsaWay.id!)?.length && (
                <>
                  <Separator />
                  <NodeList />
                </>
              )}

            {selectedOsaWay?.lifeCycleStage === LifeCycleStage.Review && (
              <>
                <Separator />
                <ViewComments />
              </>
            )}
          </Stack>
        )}

        {!isHome && !mapQueryStore.active && (
          <Stack direction={"row"} gap={0}>
            <ToggleVisibilityButton
              component={MapComponent.Filters}
              arrowAngleWhenOpen={0}
            />
            {mapStore.componentVisibility.filters && <FiltersBar />}
          </Stack>
        )}
      </HStack>

      <LoadingHeader />

      {campaignSelectionModal.open && (
        <CampaignSelectionModal onClose={campaignSelectionModal.onClose} />
      )}
    </Flex>
  );
};

export const MapPage = observer(MapPageBase);
